import {
	action,
	computed,
	makeObservable,
	observable,
	runInAction
} from 'mobx';
import {
	lmsAxiosClient,
	lmsDirectoryAxiosClient
} from 'LearningManager/service/lmsClient';
import DIRECTORY_API from 'Services/Api/Directory/Api';
import { DirectoryLmsInstance } from 'Services/Api/Directory/Types';
import USER_DASHBOARD_API from 'Services/Api/UserDashboard/Api';
import {
	ActivityAssignment,
	LaunchActivityResult,
	LearningPathAssignment,
	ListActivityAssignmentsParams,
	ListActivityAssignmentsResponse,
	ListLearningPathAssignmentsParams,
	ListLearningPathAssignmentsResponse
} from 'Services/Api/UserDashboard/Types';
import { Status } from 'app-types';
import { getOAuthToken } from 'Helpers/api';
import { systemsStore } from './SystemsStore';

class UserDashboardStore {
	status = {
		listLearningPathAssignments: Status.Idle,
		listActivityAssignments: Status.Idle,
		listLmsInstances: Status.Idle
	};

	learningPathAssignments: LearningPathAssignment[] = [];
	learningPathAssignmentsTotal = 0;
	activityAssignments: ActivityAssignment[] = [];
	activityAssignmentsTotal = 0;
	lmsInstances: DirectoryLmsInstance[] = [];

	constructor() {
		makeObservable(this, {
			status: observable,
			learningPathAssignments: observable,
			learningPathAssignmentsTotal: observable,
			activityAssignments: observable,
			activityAssignmentsTotal: observable,
			lmsInstances: observable,
			activityAssignmentsById: computed
		});
	}

	get activityAssignmentsById(): Record<number, ActivityAssignment> {
		return this.activityAssignments.reduce<
			Record<number, ActivityAssignment>
		>((acc, activity) => {
			acc[activity.id] = activity;
			return acc;
		}, {});
	}

	@action
	async fetchLearningPathAssignments(
		params: ListLearningPathAssignmentsParams = { offset: 0 }
	): Promise<void> {
		runInAction(() => {
			this.status.listLearningPathAssignments = Status.Pending;
		});
		try {
			const {
				data
			} = await lmsAxiosClient.get<ListLearningPathAssignmentsResponse>(
				USER_DASHBOARD_API.LEARNING_PATH_ASSIGNMENTS,
				{ params }
			);
			runInAction(() => {
				this.learningPathAssignments = data.data;
				this.learningPathAssignmentsTotal = data.meta.total;
				this.status.listLearningPathAssignments = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.listLearningPathAssignments = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchActivityAssignments(
		params: ListActivityAssignmentsParams = { offset: 0 }
	): Promise<void> {
		runInAction(() => {
			this.status.listActivityAssignments = Status.Pending;
		});
		try {
			const {
				data
			} = await lmsAxiosClient.get<ListActivityAssignmentsResponse>(
				USER_DASHBOARD_API.ACTIVITY_ASSIGNMENTS,
				{ params }
			);
			runInAction(() => {
				this.activityAssignments = data.data;
				this.activityAssignmentsTotal = data.meta.total;
				this.status.listActivityAssignments = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.listActivityAssignments = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchLmsInstances(): Promise<void> {
		runInAction(() => {
			this.status.listLmsInstances = Status.Pending;
		});
		try {
			const { data } = await lmsDirectoryAxiosClient.get<
				DirectoryLmsInstance[]
			>(DIRECTORY_API.LMS_INSTANCES);
			runInAction(() => {
				this.lmsInstances = data;
				this.status.listLmsInstances = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.listLmsInstances = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchActivitiesCategories(): Promise<void> {
		try {
			return await lmsAxiosClient.get(USER_DASHBOARD_API.ACTIVITY_CATEGORIES, {
				params: {
					offset: 0
				}
			});
		} catch (e) {
			throw e;
		}
	}

	// Opens an activity in the LMS SCORM player popup.
	// {system.url}/{tenant_url_postfix}/rlink.php?page=course&courseId={lms_activity_id}&token={oauth_token}
	async launchActivity(
		activity: ActivityAssignment,
		tenantPostfix?: string,
		onClose?: () => void
	): Promise<LaunchActivityResult> {
		const systemUrl = systemsStore.systems[0]?.url;
		if (!systemUrl) return 'missing_system';
		const tenantPath = tenantPostfix?.replace(/^\/+|\/+$/g, '');
		if (!tenantPath) return 'missing_tenant';

		const popup = window.open(
			'',
			'_blank',
			`width=${activity.popup_width}, height=${activity.popup_height}, resizable=yes, scrollbars=yes, menubar=no, status=no`
		);

		if (!popup) return 'popup_blocked';

		try {
			const token = await getOAuthToken();
			const baseUrl = systemUrl.replace(/\/+$/, '');
			const params = new URLSearchParams({
				page: 'course',
				courseId: String(activity.lms_activity_id),
				token: token ?? ''
			});
			const url = `${baseUrl}/${encodeURIComponent(
				tenantPath
			)}/rlink.php?${params}`;
			console.log('Course launch URL:', url);
			popup.location.href = url;
			popup.focus();
			const timer = window.setInterval(() => {
				if (popup.closed) {
					window.clearInterval(timer);
					console.log('Popup closed');
					onClose?.();
				}
			}, 500);
			return 'launched';
		} catch {
			popup.close();
			return 'launch_failed';
		}
	}
}

export const userDashboardStore = new UserDashboardStore();
export default UserDashboardStore;